import Axhelper from '@/lib/Axhelper'

class UzytkownicyService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			uzytkownicyAll: '/api/uzytkownicy/filters',
			uzytkownicyDetails: '/api/uzytkownicy/details',
			uzytkownicySearch: '/api/uzytkownicy/filters',
			uzytkownicyCreate: '/api/uzytkownicy',
			uzytkownicyUpdate: '/api/uzytkownicy',
			uzytkownicyDelete: '/api/uzytkownicy',
		}
	}

	_buildParams({ limit, sortColumn, sortDirection, filters = {} }) {
		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		if (filters?.isTrainer === true || filters?.isTrainer === 'true') {
			params.append('isTrainer', 'true')
		}

		return params
	}

	_isTrainer(user) {
		return user?.isTrainer === true || user?.isTrainer === 'true' || user?.isTrainer === 1 || user?.isTrainer === '1'
	}

	_filterTrainers(items, filters = {}) {
		const rows = Array.isArray(items) ? items : []
		if (filters?.isTrainer !== true && filters?.isTrainer !== 'true') return rows
		return rows.filter(item => this._isTrainer(item))
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.uzytkownicyAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const data = this._filterTrainers(response.data || [], filters)
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: data.length

			return {
				data,
				error: null,
				totalCount,
				hasMore: data.length < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('UzytkownicyService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch uzytkownicy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(uzytkownicyId) {
		try {
			if (!uzytkownicyId) {
				return {
					data: null,
					error: 'Invalid uzytkownicy ID provided',
				}
			}

			const url = `${this.endpoints.uzytkownicyDetails}/${encodeURIComponent(uzytkownicyId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('UzytkownicyService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch uzytkownicy details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.uzytkownicySearch}?searchText=${encodeURIComponent(searchTerm)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const data = this._filterTrainers(response.data || [], filters)
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: data.length

			return {
				data,
				error: null,
				totalCount,
				hasMore: data.length < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('UzytkownicyService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search uzytkownicy',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(uzytkownicyData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.uzytkownicyCreate, uzytkownicyData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('UzytkownicyService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create uzytkownicy',
			}
		}
	}

	async update(uzytkownicyId, uzytkownicyData) {
		try {
			if (!uzytkownicyId) {
				return {
					success: false,
					error: 'Invalid uzytkownicy ID provided',
				}
			}

			const url = `${this.endpoints.uzytkownicyUpdate}/${encodeURIComponent(uzytkownicyId)}`
			const response = await Axhelper.sendPutAsync(url, uzytkownicyData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('UzytkownicyService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update uzytkownicy',
			}
		}
	}

	async delete(uzytkownicyId) {
		try {
			if (!uzytkownicyId) {
				return {
					success: false,
					error: 'Invalid uzytkownicy ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.uzytkownicyDelete}/${encodeURIComponent(uzytkownicyId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Uzytkownicy record deleted successfully',
			}
		} catch (error) {
			console.error('UzytkownicyService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete uzytkownicy',
				message: null,
			}
		}
	}
}

export default new UzytkownicyService()
